import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../api';
import {
  type Template,
  templateBodyParamCount,
  templateUrlButtonParams,
  templateIsSendableInApp,
  templateHeaderFormat,
  unsendableReason,
  renderTemplatePreview,
} from '../templates';
import { IconClose } from './Icon';

interface Props {
  conversationId: string;
  onClose: () => void;
}

export function TemplatePickerModal({ conversationId, onClose }: Props) {
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Template | null>(null);
  const [bodyParams, setBodyParams] = useState<string[]>([]);
  const [buttonParams, setButtonParams] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [headerMediaId, setHeaderMediaId] = useState<string | null>(null);
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [headerError, setHeaderError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    api
      .getTemplates(conversationId)
      .then((r) => {
        if (alive) setTemplates(r.templates);
      })
      .catch((e) => {
        if (alive) setLoadError(e instanceof ApiError ? e.code : 'load_failed');
      });
    return () => {
      alive = false;
    };
  }, [conversationId]);

  const urlButtons = useMemo(
    () => (selected ? templateUrlButtonParams(selected.components) : []),
    [selected],
  );
  const headerFmt = selected ? templateHeaderFormat(selected.components) : null;
  const needsHeaderMedia = headerFmt === 'IMAGE' || headerFmt === 'VIDEO' || headerFmt === 'DOCUMENT';
  const headerAccept = headerFmt === 'IMAGE' ? 'image/*' : headerFmt === 'VIDEO' ? 'video/*' : '*/*';

  function pick(t: Template) {
    setSelected(t);
    setBodyParams(Array(templateBodyParamCount(t.components)).fill(''));
    setButtonParams(Array(templateUrlButtonParams(t.components).length).fill(''));
    setSendError(null);
    setHeaderMediaId(null);
    setUploadingHeader(false);
    setHeaderError(null);
  }

  async function onHeaderFile(file: File) {
    setUploadingHeader(true);
    setHeaderError(null);
    try {
      const { mediaId } = await api.uploadTemplateHeaderMedia(conversationId, file);
      setHeaderMediaId(mediaId);
    } catch (e) {
      setHeaderError(e instanceof ApiError ? e.code : 'upload_failed');
    } finally {
      setUploadingHeader(false);
    }
  }

  async function send() {
    if (!selected) return;
    setSending(true);
    setSendError(null);
    try {
      await api.sendTemplate(conversationId, {
        templateName: selected.name,
        language: selected.language,
        bodyParams,
        buttonParams,
        headerMediaId: headerMediaId ?? undefined,
      });
      onClose();
    } catch (e) {
      setSendError(e instanceof ApiError ? e.code : 'send_failed');
      setSending(false);
    }
  }

  const preview = selected ? renderTemplatePreview(selected.components, bodyParams) : '';
  const canSend =
    !!selected &&
    !sending &&
    !uploadingHeader &&
    (!needsHeaderMedia || !!headerMediaId) &&
    bodyParams.every((p) => p.trim()) &&
    buttonParams.every((p) => p.trim());

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-up"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg max-h-[80vh] flex flex-col rounded-2xl glass-chrome hairline shadow-2xl"
      >
        <header className="px-5 py-4 hairline flex items-center justify-between shrink-0">
          <h2 className="text-base font-semibold tracking-tight">Send a template</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-xl text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition flex items-center justify-center"
          >
            <IconClose size={13} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {loadError && (
            <p className="text-[13px] text-red-400">Couldn't load templates ({loadError}).</p>
          )}
          {!templates && !loadError && (
            <p className="text-[13px] text-zinc-400">Loading templates...</p>
          )}
          {templates && templates.length === 0 && (
            <p className="text-[13px] text-zinc-400">No templates registered yet.</p>
          )}

          {templates &&
            !selected &&
            templates.map((t) => {
              const ok = templateIsSendableInApp(t);
              return (
                <button
                  key={t.id}
                  disabled={!ok}
                  onClick={() => pick(t)}
                  className={`w-full text-left rounded-xl px-3 py-2 hairline transition ${
                    ok ? 'hover:bg-zinc-900/50 cursor-pointer' : 'opacity-50 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[13px] font-medium truncate">
                      {t.name} <span className="text-zinc-500">- {t.language}</span>
                    </span>
                    {t.category && (
                      <span className="text-[10px] text-emerald-300/80 shrink-0">{t.category}</span>
                    )}
                  </div>
                  {!ok && (
                    <div className="text-[11px] text-amber-300/80 mt-0.5">{unsendableReason(t)}</div>
                  )}
                </button>
              );
            })}

          {selected && (
            <div className="space-y-3">
              <div className="text-[13px] font-medium">
                {selected.name} <span className="text-zinc-500">- {selected.language}</span>
              </div>
              {needsHeaderMedia && (
                <label className="block">
                  <span className="text-[11px] text-zinc-400">Header {headerFmt?.toLowerCase()}</span>
                  <input
                    type="file"
                    accept={headerAccept}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void onHeaderFile(f);
                    }}
                    className="mt-1 w-full text-[12px] text-zinc-300 file:mr-3 file:rounded-lg file:border-0 file:bg-zinc-800 file:px-3 file:py-1.5 file:text-zinc-200"
                  />
                  {uploadingHeader && <span className="text-[11px] text-zinc-400">Uploading...</span>}
                  {headerMediaId && !uploadingHeader && (
                    <span className="text-[11px] text-emerald-300">Header uploaded</span>
                  )}
                  {headerError && (
                    <span className="text-[11px] text-red-400">Upload failed ({headerError})</span>
                  )}
                </label>
              )}
              {bodyParams.map((v, i) => (
                <label key={`b${i}`} className="block">
                  <span className="text-[11px] text-zinc-400">Body variable {`{{${i + 1}}}`}</span>
                  <input
                    value={v}
                    onChange={(e) =>
                      setBodyParams((p) => p.map((x, j) => (j === i ? e.target.value : x)))
                    }
                    className="mt-1 w-full bg-zinc-900/80 rounded-xl hairline px-3 py-2 text-[13px] focus:outline-none focus:border-indigo-500"
                  />
                </label>
              ))}
              {urlButtons.map((b, i) => (
                <label key={`btn${i}`} className="block">
                  <span className="text-[11px] text-zinc-400">Button link variable - {b.label}</span>
                  <input
                    value={buttonParams[i] ?? ''}
                    onChange={(e) =>
                      setButtonParams((p) => p.map((x, j) => (j === i ? e.target.value : x)))
                    }
                    className="mt-1 w-full bg-zinc-900/80 rounded-xl hairline px-3 py-2 text-[13px] focus:outline-none focus:border-indigo-500"
                  />
                </label>
              ))}
              {preview && (
                <div className="rounded-xl bg-zinc-900/60 hairline px-3 py-2 text-[12px] text-zinc-300">
                  <span className="text-zinc-500">Preview: </span>
                  {preview}
                </div>
              )}
              {sendError && <p className="text-[12px] text-red-400">Couldn't send ({sendError}).</p>}
            </div>
          )}
        </div>
        {selected && (
          <footer className="px-5 py-3 hairline flex items-center justify-between shrink-0">
            <button
              onClick={() => setSelected(null)}
              className="text-[13px] text-zinc-400 hover:text-zinc-100 transition"
            >
              Back
            </button>
            <button
              onClick={() => void send()}
              disabled={!canSend}
              className={`rounded-xl px-4 py-2 text-[13px] font-medium transition ${
                canSend
                  ? 'bg-emerald-500 text-zinc-950 hover:bg-emerald-400'
                  : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
              }`}
            >
              {sending ? 'Sending...' : 'Send template'}
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}
